import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { faPen, faPlus } from '@fortawesome/free-solid-svg-icons';
import ColumnVisibilityModal from '../../components/ColumnVisibilityModal';
import { Link } from 'react-router-dom';
import Table from '../../components/Table';
import Pagination from '../../components/Pagination';
import CursoFilterBar from '../../components/CursoFilterBar';
import cursoService from '../../services/cursoService';
import { useAuth } from '../../contexts/AuthContext';

// Definir columns fora do componente para evitar recriações
const columns = [
  { accessor: 'nome', Header: 'Nome', sortable: true },
  { accessor: 'padraoMatricula', Header: 'Padrão Matrícula', sortable: true,
    Cell: (value, row) => value ? `${value} (próx: ${row.proximoNumeroMatricula || 1})` : 'Não configurado' },
  { accessor: 'cadastradoEm', Header: 'Cadastrado em', sortable: true,
    Cell: (value) => value ? new Date(value).toLocaleDateString('pt-BR') : '-' }
];

const CursosList = () => {
  const { hasPermission } = useAuth();
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    nome: ''
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(['nome', 'padraoMatricula', 'cadastradoEm']);

  const pageSizes = [5, 10, 25, 50];

  // Carregar todos os cursos uma vez
  useEffect(() => {
    const loadAllCursos = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Buscar todos os cursos sem paginação
        const response = await cursoService.list({ page: 1, limit: 1000 });
        
        if (response.success) {
          setCursos(response.data);
        } else {
          setError('Erro ao carregar cursos');
        }
      } catch (err) {
        console.error('Erro ao carregar cursos:', err);
        setError(err.message || 'Erro ao carregar cursos');
      } finally {
        setLoading(false);
      }
    };

    loadAllCursos();
  }, []);
  // Modal handlers
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const handleToggleColumn = (accessor) => {
    setVisibleColumns(prev =>
      prev.includes(accessor)
        ? prev.filter(col => col !== accessor)
        : [...prev, accessor]
    );
  };

  // Filtragem local (como na tela de Extensões)
  const filteredCursos = cursos.filter((curso) => {
    return (!filters.nome || curso.nome.toLowerCase().includes(filters.nome.toLowerCase()));
  });

  // Paginação local
  const totalPages = Math.ceil(filteredCursos.length / pageSize);
  const paginatedCursos = filteredCursos.slice((page - 1) * pageSize, page * pageSize);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        <div className="px-2 sm:px-6">
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-600">Carregando cursos...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        <div className="px-2 sm:px-6">
          <div className="flex justify-center items-center h-64">
            <div className="text-red-600">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!hasPermission('cursos.list')) {
    return (
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        <div className="px-2 sm:px-6">
          <div className="flex justify-center items-center h-64">
            <div className="text-red-600">Acesso negado: você não tem permissão para visualizar cursos.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        <div className="px-2 sm:px-6">
          <div className="max-w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Listagem de Cursos</h2>
              <Link
                to="/cursos/novo"
                className="px-4 py-2 rounded bg-green-600 text-white font-semibold shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faPlus} />
                <span>Novo Curso</span>
              </Link>
            </div>
            <ColumnVisibilityModal
              columns={columns}
              visibleColumns={visibleColumns}
              onChange={handleToggleColumn}
              onClose={handleCloseModal}
              isOpen={isModalOpen}
            />
            <div className="max-w-full">
              <CursoFilterBar 
                filters={filters} 
                onChange={handleFilterChange}
              />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2 max-w-full">
              <div className="order-2 sm:order-1 w-full sm:w-auto">
                <Pagination currentPage={page} totalPages={totalPages} onPageChange={handlePageChange} />
              </div>
              <div className="order-1 sm:order-2 flex items-center w-full sm:w-auto justify-end gap-2">
                <label htmlFor="pageSize" className="text-sm text-gray-700 whitespace-nowrap mr-2">Por página:</label>
                <select
                  id="pageSize"
                  value={pageSize}
                  onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
                >
                  {pageSizes.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="flex items-center gap-2 px-3 py-1 rounded bg-green-600 text-white font-medium shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
                  onClick={handleOpenModal}
                  aria-label="Exibir/Ocultar colunas"
                >
                  <FontAwesomeIcon icon={faEye} />
                  <span>Colunas</span>
                </button>
              </div>
            </div>
          <div className="max-w-full">
            <Table
              columns={columns.filter(col => visibleColumns.includes(col.accessor)).map(col => {
                if (col.accessor === 'nome') {
                  return {
                    ...col,
                    Cell: (value, row) => (
                      <div className="flex items-center gap-2">
                        <span>{value}</span>
                        <Link
                          to={`/cursos/${row.idCurso}/edit`}
                          className="text-green-600 hover:text-green-800 p-1 rounded focus:outline-none"
                          title="Editar"
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </Link>
                      </div>
                    )
                  };
                }
                return col;
              })}
              data={paginatedCursos}
            />
          </div>
          
          {filteredCursos.length > 0 && (
            <div className="mt-4 text-sm text-gray-600">
              Mostrando {((page - 1) * pageSize) + 1} a {Math.min(page * pageSize, filteredCursos.length)} de {filteredCursos.length} cursos
            </div>
          )}
          </div>
        </div>
      </div>
    
  );
};

export default CursosList;
